import { LitElement, html, css, type PropertyValues } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { dispatch } from '../../utils/event-helpers';
import { generateUid } from '../../utils/uid';
import { DatePickerController } from './date-picker.controller';

type DatePickerType = 'single' | 'dual';

// Calendar SVG (20×20 outline style, matches Untitled UI)
const CALENDAR_ICON = html`<svg
  xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none"
  aria-hidden="true" focusable="false"
>
  <path d="M14 2V5M6 2V5M2.5 7.5h15M3.5 3.5h13a1 1 0 0 1 1 1v12a1 1 0 0 1-1 1h-13a1 1 0 0 1-1-1v-12a1 1 0 0 1 1-1z"
    stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
</svg>`;

const CHEVRON_LEFT = html`<svg
  xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none"
  aria-hidden="true" focusable="false"
>
  <path d="M12.5 15l-5-5 5-5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
</svg>`;

const CHEVRON_RIGHT = html`<svg
  xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none"
  aria-hidden="true" focusable="false"
>
  <path d="M7.5 5l5 5-5 5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
</svg>`;

/**
 * An interactive date-selection control with a compact trigger button and an
 * absolutely-positioned calendar panel.
 *
 * Supports two modes — `single` (one date) and `dual` (start/end date range).
 * In `dual` mode a preset sidebar with nine shortcuts (Today → All time) renders
 * on the left on desktop and collapses to a horizontal scrollable strip
 * on mobile (< 560px). All calendar state logic is delegated to `DatePickerController`.
 *
 * @element asc-date-picker
 *
 * @fires asc-change  - Fired when Apply is clicked with a valid selection.
 *   Single: `detail: { date: string }`.
 *   Dual: `detail: { startDate: string, endDate: string }`.
 * @fires asc-cancel  - Fired when Cancel is clicked or the panel is dismissed. No detail.
 * @fires asc-select  - Fired on every day-cell or preset click (uncommitted).
 *   Single: `detail: { date: string | null }`.
 *   Dual: `detail: { startDate: string | null, endDate: string | null }`.
 *
 * @csspart trigger           - The trigger `<button>`.
 * @csspart trigger-icon      - Calendar icon wrapper span.
 * @csspart trigger-label     - Label area wrapper span.
 * @csspart trigger-value     - Confirmed-date text span (aria-live region).
 * @csspart panel             - The floating `<div popover>`.
 * @csspart preset-sidebar    - `<nav>` containing preset buttons (dual only).
 * @csspart preset-item       - Individual preset `<button>`.
 * @csspart panel-body        - Flex row wrapping preset sidebar + calendars (dual only).
 * @csspart calendars         - Container for one or two calendar grids.
 * @csspart calendar-wrapper  - Individual calendar wrapper div.
 * @csspart calendar-nav      - Month navigation header row.
 * @csspart nav-prev          - Previous-month `<button>`.
 * @csspart nav-next          - Next-month `<button>`.
 * @csspart month-label       - Month + year heading `<span>`.
 * @csspart calendar          - Calendar `<table role="grid">`.
 * @csspart weekday-header    - `<thead>` weekday row.
 * @csspart day-cell          - Individual `<td role="gridcell">`.
 * @csspart footer            - Bottom bar with date inputs and/or action buttons.
 * @csspart date-input-row    - Single mode: wrapper for date input + Today button.
 * @csspart date-inputs       - Dual mode: wrapper for start/end text inputs in footer.
 * @csspart date-input        - Start (or single) date `<input>`.
 * @csspart date-input-end    - End date `<input>` (dual only).
 * @csspart date-separator    - En-dash span between dual inputs.
 * @csspart today-button      - "Today" shortcut `<button>` (single only).
 * @csspart footer-actions    - Cancel + Apply button wrapper.
 * @csspart apply-button      - Apply `<button>`.
 * @csspart cancel-button     - Cancel `<button>`.
 *
 * @cssprop --asc-date-picker-trigger-bg                   - Trigger background (placeholder state). Default: `var(--asc-bg-primary)`
 * @cssprop --asc-date-picker-trigger-bg-active            - Trigger background (active/confirmed state). Default: `var(--asc-bg-primary)`
 * @cssprop --asc-date-picker-trigger-border-color         - Trigger border (placeholder). Default: `var(--asc-border-primary)`
 * @cssprop --asc-date-picker-trigger-border-color-active  - Trigger border (confirmed). Default: `var(--asc-border-brand)`
 * @cssprop --asc-date-picker-trigger-border-radius        - Trigger corner radius. Default: `var(--asc-radius-sm)`
 * @cssprop --asc-date-picker-trigger-padding-x            - Trigger horizontal padding. Default: `var(--asc-spacing-lg)`
 * @cssprop --asc-date-picker-trigger-padding-y            - Trigger vertical padding. Default: `var(--asc-spacing-md)`
 * @cssprop --asc-date-picker-trigger-shadow               - Trigger drop shadow. Default: `0 1px 2px 0 var(--asc-shadow-xs)`
 * @cssprop --asc-date-picker-trigger-color-placeholder    - Placeholder text color. Default: `var(--asc-text-placeholder)`
 * @cssprop --asc-date-picker-trigger-color-active         - Confirmed value text color. Default: `var(--asc-text-secondary)`
 * @cssprop --asc-date-picker-trigger-icon-color           - Calendar icon color. Default: `var(--asc-fg-secondary)`
 * @cssprop --asc-date-picker-trigger-focus-ring           - Trigger focus ring. Default: `var(--asc-focus-ring)`
 * @cssprop --asc-date-picker-panel-bg                     - Panel surface fill. Default: `var(--asc-bg-primary)`
 * @cssprop --asc-date-picker-panel-border-color           - Panel border. Default: `var(--asc-border-tertiary)`
 * @cssprop --asc-date-picker-panel-border-radius          - Panel corner radius. Default: `var(--asc-radius-2xl)`
 * @cssprop --asc-date-picker-panel-shadow                 - Panel elevation shadow. Default: `0 20px 24px -4px var(--asc-shadow-xl_01), 0 8px 8px -4px var(--asc-shadow-xl_02)`
 * @cssprop --asc-date-picker-input-bg                     - Date input fill. Default: `var(--asc-bg-primary)`
 * @cssprop --asc-date-picker-input-border-color           - Date input border. Default: `var(--asc-border-primary)`
 * @cssprop --asc-date-picker-input-border-color-error     - Date input error border. Default: `var(--asc-border-error)`
 * @cssprop --asc-date-picker-input-border-radius          - Date input corner radius. Default: `var(--asc-radius-sm)`
 * @cssprop --asc-date-picker-input-padding-x              - Date input horizontal padding. Default: `var(--asc-spacing-lg)`
 * @cssprop --asc-date-picker-input-color                  - Date input text color. Default: `var(--asc-text-secondary)`
 * @cssprop --asc-date-picker-separator-color              - En-dash separator color. Default: `var(--asc-text-quaternary)`
 * @cssprop --asc-date-picker-today-btn-bg                 - Today button fill. Default: `var(--asc-bg-primary)`
 * @cssprop --asc-date-picker-today-btn-bg-hover           - Today button hover fill. Default: `var(--asc-bg-primary_hover)`
 * @cssprop --asc-date-picker-today-btn-border-color       - Today button border. Default: `var(--asc-border-primary)`
 * @cssprop --asc-date-picker-today-btn-color              - Today button text. Default: `var(--asc-text-secondary)`
 * @cssprop --asc-date-picker-today-btn-border-radius      - Today button corner radius. Default: `var(--asc-radius-sm)`
 * @cssprop --asc-date-picker-today-btn-focus-ring         - Today button focus ring. Default: `var(--asc-focus-ring)`
 * @cssprop --asc-date-picker-month-label-color            - Month/year heading text. Default: `var(--asc-fg-secondary)`
 * @cssprop --asc-date-picker-nav-color                    - Nav chevron fill. Default: `var(--asc-fg-secondary)`
 * @cssprop --asc-date-picker-nav-hover-bg                 - Nav button hover fill. Default: `var(--asc-bg-primary_hover)`
 * @cssprop --asc-date-picker-nav-border-radius            - Nav button corner radius. Default: `var(--asc-radius-sm)`
 * @cssprop --asc-date-picker-nav-focus-ring               - Nav button focus ring. Default: `var(--asc-focus-ring)`
 * @cssprop --asc-date-picker-weekday-color                - Weekday header text. Default: `var(--asc-text-secondary)`
 * @cssprop --asc-date-picker-day-color                    - Default day text. Default: `var(--asc-text-secondary)`
 * @cssprop --asc-date-picker-day-color-muted              - Adjacent-month day text. Default: `var(--asc-text-disabled)`
 * @cssprop --asc-date-picker-day-size                     - Day cell width/height. Default: `40px`
 * @cssprop --asc-date-picker-day-border-radius            - Day cell corner radius. Default: `var(--asc-radius-full)`
 * @cssprop --asc-date-picker-day-hover-bg                 - Day hover fill. Default: `var(--asc-bg-primary_hover)`
 * @cssprop --asc-date-picker-day-today-dot-color          - Today indicator dot. Default: `var(--asc-fg-brand-primary)`
 * @cssprop --asc-date-picker-day-selected-bg              - Selected/start/end day fill. Default: `var(--asc-bg-brand-solid)`
 * @cssprop --asc-date-picker-day-selected-color           - Selected/start/end day text. Default: `var(--asc-text-white)`
 * @cssprop --asc-date-picker-day-range-bg                 - In-range band fill (dual). Default: `var(--asc-bg-active)`
 * @cssprop --asc-date-picker-day-range-color              - In-range day text (dual). Default: `var(--asc-text-secondary)`
 * @cssprop --asc-date-picker-day-focus-ring               - Day cell focus ring. Default: `var(--asc-focus-ring)`
 * @cssprop --asc-date-picker-sidebar-border-color         - Sidebar/calendar separator. Default: `var(--asc-border-secondary)`
 * @cssprop --asc-date-picker-sidebar-width                - Preset sidebar fixed width. Default: `152px`
 * @cssprop --asc-date-picker-preset-color                 - Preset button text. Default: `var(--asc-text-secondary)`
 * @cssprop --asc-date-picker-preset-hover-bg              - Preset button hover fill. Default: `var(--asc-bg-primary_hover)`
 * @cssprop --asc-date-picker-preset-active-bg             - Active preset fill. Default: `var(--asc-bg-active)`
 * @cssprop --asc-date-picker-preset-active-color          - Active preset text. Default: `var(--asc-text-secondary_hover)`
 * @cssprop --asc-date-picker-preset-border-radius         - Preset button corner radius. Default: `var(--asc-radius-sm)`
 * @cssprop --asc-date-picker-preset-focus-ring            - Preset button focus ring. Default: `var(--asc-focus-ring)`
 * @cssprop --asc-date-picker-apply-bg                     - Apply button fill. Default: `var(--asc-bg-brand-solid)`
 * @cssprop --asc-date-picker-apply-bg-hover               - Apply hover fill. Default: `var(--asc-bg-brand-solid_hover)`
 * @cssprop --asc-date-picker-apply-color                  - Apply text color. Default: `var(--asc-text-white)`
 * @cssprop --asc-date-picker-apply-border-radius          - Apply corner radius. Default: `var(--asc-radius-sm)`
 * @cssprop --asc-date-picker-apply-focus-ring             - Apply focus ring. Default: `var(--asc-focus-ring)`
 * @cssprop --asc-date-picker-cancel-bg                    - Cancel button fill. Default: `var(--asc-bg-primary)`
 * @cssprop --asc-date-picker-cancel-bg-hover              - Cancel hover fill. Default: `var(--asc-bg-primary_hover)`
 * @cssprop --asc-date-picker-cancel-border-color          - Cancel border. Default: `var(--asc-border-primary)`
 * @cssprop --asc-date-picker-cancel-color                 - Cancel text color. Default: `var(--asc-text-secondary)`
 * @cssprop --asc-date-picker-cancel-border-radius         - Cancel corner radius. Default: `var(--asc-radius-sm)`
 * @cssprop --asc-date-picker-cancel-focus-ring            - Cancel focus ring. Default: `var(--asc-focus-ring)`
 * @cssprop --asc-date-picker-footer-gap                   - Gap between Apply and Cancel. Default: `var(--asc-spacing-lg)`
 */
@customElement('asc-date-picker')
export class AscDatePicker extends LitElement {
  static override styles = css`
    /* ═══════════════════════════════════════════════════════════
       HOST — anchor for absolutely positioned panel
       ═══════════════════════════════════════════════════════════ */
    :host {
      display: inline-block;
      position: relative;
    }

    /* ═══════════════════════════════════════════════════════════
       TRIGGER
       ═══════════════════════════════════════════════════════════ */

    [part='trigger'] {
      display: inline-flex;
      align-items: center;
      gap: var(--asc-spacing-md);
      cursor: pointer;
      padding:
        var(--asc-date-picker-trigger-padding-y, var(--asc-spacing-md))
        var(--asc-date-picker-trigger-padding-x, var(--asc-spacing-lg));
      background-color: var(--asc-date-picker-trigger-bg, var(--asc-bg-primary));
      border: var(--asc-border-width) solid
        var(--asc-date-picker-trigger-border-color, var(--asc-border-primary));
      border-radius: var(--asc-date-picker-trigger-border-radius, var(--asc-radius-sm));
      box-shadow: var(--asc-date-picker-trigger-shadow, 0 1px 2px 0 var(--asc-shadow-xs));
      font-family: var(--asc-font-family-body);
      font-size: var(--asc-font-size-text-sm);
      font-weight: var(--asc-font-weight-medium);
      line-height: var(--asc-line-height-text-sm);
      color: var(--asc-date-picker-trigger-color-placeholder, var(--asc-text-placeholder));
      transition:
        border-color var(--asc-transition-fast),
        color var(--asc-transition-fast),
        box-shadow var(--asc-transition-fast);
    }

    [part='trigger']:focus-visible {
      outline: none;
      box-shadow:
        0 0 0 2px var(--asc-bg-primary),
        0 0 0 4px var(--asc-date-picker-trigger-focus-ring, var(--asc-focus-ring));
    }

    [part='trigger']:disabled {
      cursor: not-allowed;
      opacity: 0.5;
    }

    :host([open]) [part='trigger'],
    [part='trigger'][data-active] {
      border-color: var(--asc-date-picker-trigger-border-color-active, var(--asc-border-brand));
      color: var(--asc-date-picker-trigger-color-active, var(--asc-text-secondary));
    }

    /* Has a confirmed value — show date text in the active/secondary color */
    [part='trigger'].trigger--has-value {
      color: var(--asc-date-picker-trigger-color-active, var(--asc-text-secondary));
      font-weight: var(--asc-font-weight-semibold);
    }

    [part='trigger-icon'] {
      display: flex;
      align-items: center;
      justify-content: center;
      color: var(--asc-date-picker-trigger-icon-color, var(--asc-fg-secondary));
      flex-shrink: 0;
    }

    [part='trigger-label'] {
      display: flex;
      align-items: center;
    }

    /* ═══════════════════════════════════════════════════════════
       PANEL — absolutely positioned below trigger
       ═══════════════════════════════════════════════════════════ */

    [part='panel'] {
      display: none;
      flex-direction: column;
      position: absolute;
      top: calc(100% + 6px);
      left: 0;
      z-index: var(--asc-z-popover);
      background-color: var(--asc-date-picker-panel-bg, var(--asc-bg-primary));
      border: var(--asc-border-width) solid var(--asc-border-tertiary);
      border-radius: var(--asc-radius-2xl);
      box-shadow:
        var(--asc-date-picker-panel-shadow,
          0 20px 24px -4px var(--asc-shadow-xl_01),
          0 8px 8px -4px var(--asc-shadow-xl_02),
          0 3px 3px -1.5px var(--asc-shadow-xl_03));
      overflow: hidden;
      min-width: 300px;
    }

    :host([open]) [part='panel'] {
      display: flex;
    }

    /* ═══════════════════════════════════════════════════════════
       PANEL BODY (dual: sidebar + calendars side by side)
       ═══════════════════════════════════════════════════════════ */

    [part='panel-body'] {
      display: flex;
      flex-direction: row;
      align-items: stretch;
    }

    /* ═══════════════════════════════════════════════════════════
       PRESET SIDEBAR
       ═══════════════════════════════════════════════════════════ */

    [part='preset-sidebar'] {
      display: flex;
      flex-direction: column;
      gap: 2px;
      padding: var(--asc-spacing-lg);
      border-right: var(--asc-border-width) solid var(--asc-border-secondary);
      width: var(--asc-date-picker-sidebar-width, 152px);
      flex-shrink: 0;
    }

    [part='preset-item'] {
      appearance: none;
      border: none;
      background: transparent;
      cursor: pointer;
      width: 100%;
      text-align: left;
      padding: var(--asc-spacing-sm) var(--asc-spacing-md);
      border-radius: var(--asc-radius-xs);
      font-family: var(--asc-font-family-body);
      font-size: var(--asc-font-size-text-sm);
      font-weight: var(--asc-font-weight-medium);
      line-height: var(--asc-line-height-text-sm);
      color: var(--asc-text-secondary);
      white-space: nowrap;
      transition: background-color var(--asc-transition-fast);
    }

    [part='preset-item']:hover {
      background-color: var(--asc-bg-primary_hover);
    }

    [part='preset-item'][aria-pressed='true'] {
      background-color: var(--asc-bg-active);
      color: var(--asc-text-secondary_hover);
    }

    [part='preset-item']:focus-visible {
      outline: none;
      box-shadow: 0 0 0 2px var(--asc-focus-ring);
    }

    /* ═══════════════════════════════════════════════════════════
       CALENDARS CONTAINER
       ═══════════════════════════════════════════════════════════ */

    [part='calendars'] {
      display: flex;
      flex-direction: row;
    }

    .dual-trailing {
      display: flex;
      flex-direction: column;
      flex: 1;
      min-width: 0;
    }

    [part='calendar-wrapper'] {
      display: flex;
      flex-direction: column;
      gap: var(--asc-spacing-xl);
      padding: var(--asc-spacing-2xl) var(--asc-spacing-3xl);
    }

    [part='calendar-wrapper'] + [part='calendar-wrapper'] {
      border-left: var(--asc-border-width) solid var(--asc-border-secondary);
    }

    /* ═══════════════════════════════════════════════════════════
       CALENDAR NAV
       ═══════════════════════════════════════════════════════════ */

    [part='calendar-nav'] {
      display: flex;
      align-items: center;
      justify-content: space-between;
    }

    [part='nav-prev'],
    [part='nav-next'] {
      appearance: none;
      border: none;
      background: transparent;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      padding: var(--asc-spacing-xs);
      width: 32px;
      height: 32px;
      border-radius: var(--asc-radius-xs);
      color: var(--asc-fg-secondary);
      transition: background-color var(--asc-transition-fast);
      flex-shrink: 0;
    }

    [part='nav-prev']:hover,
    [part='nav-next']:hover {
      background-color: var(--asc-bg-primary_hover);
    }

    [part='nav-prev']:focus-visible,
    [part='nav-next']:focus-visible {
      outline: none;
      box-shadow: 0 0 0 2px var(--asc-focus-ring);
    }

    [part='nav-prev'].nav--hidden,
    [part='nav-next'].nav--hidden {
      visibility: hidden;
      pointer-events: none;
    }

    [part='month-label'] {
      font-family: var(--asc-font-family-body);
      font-size: var(--asc-font-size-text-sm);
      font-weight: var(--asc-font-weight-semibold);
      line-height: var(--asc-line-height-text-sm);
      color: var(--asc-text-primary);
      text-align: center;
      flex: 1;
    }

    /* ═══════════════════════════════════════════════════════════
       CALENDAR GRID
       ═══════════════════════════════════════════════════════════ */

    [part='calendar'] {
      table-layout: fixed;
      width: calc(var(--asc-date-picker-day-size, 40px) * 7);
      border-collapse: separate;
      border-spacing: 0;
    }

    [part='weekday-header'] th {
      width: var(--asc-date-picker-day-size, 40px);
      height: 36px;
      font-family: var(--asc-font-family-body);
      font-size: var(--asc-font-size-text-xs);
      font-weight: var(--asc-font-weight-medium);
      line-height: var(--asc-line-height-text-xs);
      color: var(--asc-text-quaternary);
      text-align: center;
      padding: 0;
    }

    /* ── Day cells — <td> carries the range band ────────────── */

    [part='day-cell'] {
      padding: 0;
      width: var(--asc-date-picker-day-size, 40px);
      height: var(--asc-date-picker-day-size, 40px);
      vertical-align: middle;
      text-align: center;
    }

    /* Range band on <td> */
    [part='day-cell'].day--in-range {
      background-color: var(--asc-date-picker-day-range-bg, var(--asc-bg-active));
    }

    /* Start cap: right half band only, left corners rounded */
    [part='day-cell'].day--range-start {
      background: linear-gradient(
        to right,
        transparent 50%,
        var(--asc-date-picker-day-range-bg, var(--asc-bg-active)) 50%
      );
      border-radius: var(--asc-radius-full) 0 0 var(--asc-radius-full);
    }

    /* End cap: left half band only, right corners rounded */
    [part='day-cell'].day--range-end {
      background: linear-gradient(
        to left,
        transparent 50%,
        var(--asc-date-picker-day-range-bg, var(--asc-bg-active)) 50%
      );
      border-radius: 0 var(--asc-radius-full) var(--asc-radius-full) 0;
    }

    /* Same-day start+end: no band, no radius */
    [part='day-cell'].day--range-start.day--range-end {
      background: transparent;
      border-radius: 0;
    }

    /* ── Day button — the circle ────────────────────────────── */

    .day-btn {
      appearance: none;
      border: none;
      padding: 0;
      margin: 0;
      background: transparent;
      font-family: var(--asc-font-family-body);
      font-size: var(--asc-font-size-text-sm);
      font-weight: var(--asc-font-weight-regular);
      line-height: 1;
      color: var(--asc-text-secondary);
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      width: var(--asc-date-picker-day-size, 40px);
      height: var(--asc-date-picker-day-size, 40px);
      border-radius: var(--asc-radius-full);
      box-sizing: border-box;
      position: relative;
      transition:
        background-color var(--asc-transition-fast),
        color var(--asc-transition-fast);
    }

    .day-btn:hover {
      background-color: var(--asc-bg-primary_hover);
    }

    .day-btn:focus-visible {
      outline: none;
      box-shadow: 0 0 0 2px var(--asc-focus-ring);
    }

    .day-btn:disabled,
    [part='day-cell'].day--outside .day-btn {
      color: var(--asc-text-disabled);
      cursor: default;
      pointer-events: none;
    }

    /* Selected / range caps */
    [part='day-cell'].day--selected .day-btn {
      background-color: var(--asc-date-picker-day-selected-bg, var(--asc-bg-brand-solid));
      color: var(--asc-text-white);
      font-weight: var(--asc-font-weight-medium);
    }

    [part='day-cell'].day--selected .day-btn:hover {
      background-color: var(--asc-date-picker-day-selected-bg, var(--asc-bg-brand-solid));
    }

    /* ── Day inner (number + optional dot) ──────────────────── */

    .day__inner {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 2px;
      pointer-events: none;
    }

    .day__number {
      display: block;
      line-height: 1;
    }

    .day__dot {
      display: block;
      width: 4px;
      height: 4px;
      border-radius: var(--asc-radius-full);
      background-color: var(--asc-fg-brand-primary);
      flex-shrink: 0;
    }

    [part='day-cell'].day--selected .day__dot {
      background-color: var(--asc-text-white);
    }

    /* ═══════════════════════════════════════════════════════════
       DATE INPUT ROW (single — above calendar)
       ═══════════════════════════════════════════════════════════ */

    [part='date-input-row'] {
      display: flex;
      align-items: center;
      gap: var(--asc-spacing-md);
    }

    [part='date-input'],
    [part='date-input-end'] {
      appearance: none;
      border: var(--asc-border-width) solid var(--asc-border-primary);
      border-radius: var(--asc-radius-sm);
      padding: var(--asc-spacing-md) var(--asc-spacing-lg);
      background-color: var(--asc-bg-primary);
      font-family: var(--asc-font-family-body);
      font-size: var(--asc-font-size-text-sm);
      font-weight: var(--asc-font-weight-regular);
      line-height: var(--asc-line-height-text-sm);
      color: var(--asc-text-secondary);
      min-width: 0;
      transition: border-color var(--asc-transition-fast);
    }

    [part='date-input'] {
      flex: 1;
      width: 100%;
    }

    /* Dual mode: fixed-width footer inputs */
    .dual-trailing [part='date-input'] {
      flex: none;
      width: 136px;
    }

    [part='date-input-end'] {
      width: 136px;
    }

    [part='date-input']:focus,
    [part='date-input-end']:focus {
      outline: none;
      border-color: var(--asc-focus-ring);
      box-shadow: 0 0 0 2px color-mix(in srgb, var(--asc-focus-ring) 20%, transparent);
    }

    [part='date-input'][aria-invalid='true'],
    [part='date-input-end'][aria-invalid='true'] {
      border-color: var(--asc-border-error);
    }

    [part='today-button'] {
      appearance: none;
      border: var(--asc-border-width) solid var(--asc-border-primary);
      border-radius: var(--asc-radius-sm);
      background-color: var(--asc-bg-primary);
      padding: var(--asc-spacing-md) var(--asc-spacing-lg);
      cursor: pointer;
      font-family: var(--asc-font-family-body);
      font-size: var(--asc-font-size-text-sm);
      font-weight: var(--asc-font-weight-medium);
      line-height: var(--asc-line-height-text-sm);
      color: var(--asc-text-secondary);
      white-space: nowrap;
      transition: background-color var(--asc-transition-fast);
    }

    [part='today-button']:hover {
      background-color: var(--asc-bg-primary_hover);
    }

    [part='today-button']:focus-visible {
      outline: none;
      box-shadow: 0 0 0 2px var(--asc-focus-ring);
    }

    /* ═══════════════════════════════════════════════════════════
       FOOTER
       ═══════════════════════════════════════════════════════════ */

    [part='footer'] {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: var(--asc-spacing-lg);
      padding: var(--asc-spacing-xl) var(--asc-spacing-3xl);
      border-top: var(--asc-border-width) solid var(--asc-border-secondary);
    }

    /* Dual mode: footer lives inside trailing content — uniform padding */
    .dual-trailing [part='footer'] {
      padding: var(--asc-spacing-xl);
    }

    [part='date-inputs'] {
      display: flex;
      align-items: center;
      gap: var(--asc-spacing-lg);
      flex: 1;
      min-width: 0;
    }

    [part='date-separator'] {
      color: var(--asc-text-quaternary);
      font-size: var(--asc-font-size-text-md);
      flex-shrink: 0;
    }

    [part='footer-actions'] {
      display: flex;
      gap: var(--asc-spacing-lg);
      flex-shrink: 0;
    }

    /* Single mode: actions fill the full footer width equally */
    .footer--single [part='footer-actions'] {
      flex: 1;
    }

    .footer--single [part='apply-button'],
    .footer--single [part='cancel-button'] {
      flex: 1;
    }

    [part='apply-button'],
    [part='cancel-button'] {
      appearance: none;
      border: none;
      padding: var(--asc-spacing-md) var(--asc-spacing-xl);
      border-radius: var(--asc-radius-sm);
      cursor: pointer;
      font-family: var(--asc-font-family-body);
      font-size: var(--asc-font-size-text-sm);
      font-weight: var(--asc-font-weight-semibold);
      line-height: var(--asc-line-height-text-sm);
      white-space: nowrap;
      box-sizing: border-box;
      transition: background-color var(--asc-transition-fast);
    }

    [part='apply-button'] {
      background-color: var(--asc-bg-brand-solid);
      color: var(--asc-text-white);
    }

    [part='apply-button']:hover {
      background-color: var(--asc-bg-brand-solid_hover);
    }

    [part='apply-button']:focus-visible {
      outline: none;
      box-shadow: 0 0 0 2px var(--asc-focus-ring);
    }

    [part='cancel-button'] {
      background-color: var(--asc-bg-primary);
      color: var(--asc-text-secondary);
      border: var(--asc-border-width) solid var(--asc-border-primary);
    }

    [part='cancel-button']:hover {
      background-color: var(--asc-bg-primary_hover);
    }

    [part='cancel-button']:focus-visible {
      outline: none;
      box-shadow: 0 0 0 2px var(--asc-focus-ring);
    }
  `;

  // ── Controller ───────────────────────────────────────────────────────────

  private readonly _uid = generateUid();
  readonly _controller = new DatePickerController(this);

  // ── Properties ───────────────────────────────────────────────────────────

  /** Selection mode — single date or date range. */
  @property({ type: String, reflect: true }) type: DatePickerType = 'single';

  /** Whether the calendar panel is open. */
  @property({ type: Boolean, reflect: true }) open = false;

  /** Placeholder text shown in the trigger when no date has been confirmed. */
  @property({ type: String }) placeholder = '';

  /** Disables the trigger button and prevents the panel from opening. */
  @property({ type: Boolean, reflect: true }) disabled = false;

  // ── Internal state (written by DatePickerController) ─────────────────────

  /** @internal */ @state() _confirmedDate: string | null = null;
  /** @internal */ @state() _confirmedStartDate: string | null = null;
  /** @internal */ @state() _confirmedEndDate: string | null = null;
  /** @internal */ @state() _startInputError = false;
  /** @internal */ @state() _endInputError = false;

  // ── Forwarded ARIA state ───────────────────────────────────────────────────
  @state() private _triggerAriaLabel: string | null = null;
  @state() private _triggerAriaLabelledby: string | null = null;

  // ── Click-outside and Escape handling ─────────────────────────────────────

  private readonly _boundOutsideClick = (e: MouseEvent): void => {
    if (!this.open) return;
    if (!e.composedPath().includes(this)) {
      this.open = false;
      this._controller.cancelPending();
      dispatch(this, 'asc-cancel');
    }
  };

  private readonly _boundKeydown = (e: KeyboardEvent): void => {
    if (e.key === 'Escape' && this.open) {
      e.stopPropagation();
      this.open = false;
      this._controller.cancelPending();
      dispatch(this, 'asc-cancel');
      this.shadowRoot?.querySelector<HTMLButtonElement>('[part="trigger"]')?.focus();
    }
  };

  override connectedCallback(): void {
    super.connectedCallback();
    // Capture aria-label/aria-labelledby and forward them to the inner trigger.
    // They are stripped from the host (no role) to prevent aria-prohibited-attr violations.
    const label = this.getAttribute('aria-label');
    const labelledby = this.getAttribute('aria-labelledby');
    if (label) {
      this._triggerAriaLabel = label;
      this.removeAttribute('aria-label');
    }
    if (labelledby) {
      this._triggerAriaLabelledby = labelledby;
      this.removeAttribute('aria-labelledby');
    }
    document.addEventListener('click', this._boundOutsideClick);
    document.addEventListener('keydown', this._boundKeydown);
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback();
    document.removeEventListener('click', this._boundOutsideClick);
    document.removeEventListener('keydown', this._boundKeydown);
  }

  // ── Lifecycle ──────────────────────────────────────────────────────────

  /** CSS selector for the first focusable element in the panel. */
  private static readonly _PANEL_FOCUSABLE =
    'button:not([disabled]):not([tabindex="-1"]), input, [tabindex="0"]';

  override updated(changed: PropertyValues<this>): void {
    if (changed.has('open')) {
      if (this.open) {
        // Move focus to first focusable element in the panel when it opens
        requestAnimationFrame(() => {
          const panel = this.shadowRoot?.querySelector<HTMLElement>('[part="panel"]');
          const firstFocusable = panel?.querySelector<HTMLElement>(
            AscDatePicker._PANEL_FOCUSABLE
          );
          firstFocusable?.focus();
        });
      }
    }
  }

  // ── Event handlers ─────────────────────────────────────────────────────

  private _handleTriggerClick(): void {
    if (this.disabled) return;
    this.open = !this.open;
    if (!this.open) {
      this._controller.cancelPending();
      dispatch(this, 'asc-cancel');
    }
  }

  private _handleApply(): void {
    this._controller.confirmPending();
    if (this.type === 'single') {
      if (this._confirmedDate) {
        dispatch<{ date: string }>(this, 'asc-change', {
          detail: { date: this._confirmedDate },
        });
      }
    } else {
      if (this._confirmedStartDate && this._confirmedEndDate) {
        dispatch<{ startDate: string; endDate: string }>(this, 'asc-change', {
          detail: { startDate: this._confirmedStartDate, endDate: this._confirmedEndDate },
        });
      }
    }
    this.open = false;
  }

  private _handleCancel(): void {
    this._controller.cancelPending();
    dispatch(this, 'asc-cancel');
    this.open = false;
  }

  private _handleStartInput(e: Event): void {
    this._controller.parseInput((e.target as HTMLInputElement).value, 'start');
  }

  private _handleEndInput(e: Event): void {
    this._controller.parseInput((e.target as HTMLInputElement).value, 'end');
  }

  // ── Computed display ─────────────────────────────────────────────────────

  private get _triggerLabel(): string {
    if (this.type === 'single') {
      return this._confirmedDate ?? (this.placeholder || 'Select date');
    }
    if (this._confirmedStartDate && this._confirmedEndDate) {
      return `${this._confirmedStartDate} – ${this._confirmedEndDate}`;
    }
    return this.placeholder || 'Select date range';
  }

  private get _hasValue(): boolean {
    return this.type === 'single'
      ? this._confirmedDate !== null
      : this._confirmedStartDate !== null && this._confirmedEndDate !== null;
  }

  // ── Render ──────────────────────────────────────────────────────────────

  override render() {
    const panelId = `asc-dp-panel-${this._uid}`;
    const isDual = this.type === 'dual';

    return html`
      <button
        part="trigger"
        class=${classMap({ 'trigger--has-value': this._hasValue })}
        aria-haspopup="dialog"
        aria-expanded=${this.open ? 'true' : 'false'}
        aria-controls=${panelId}
        aria-label=${ifDefined(this._triggerAriaLabel ?? undefined)}
        aria-labelledby=${ifDefined(this._triggerAriaLabelledby ?? undefined)}
        ?disabled=${this.disabled}
        @click=${this._handleTriggerClick}
      >
        <span part="trigger-icon">${CALENDAR_ICON}</span>
        <span part="trigger-value" aria-live="polite">${this._triggerLabel}</span>
      </button>

      <div
        part="panel"
        id=${panelId}
        role="dialog"
        aria-label="Date picker"
      >
        ${isDual ? this._renderDualPanel() : this._renderSinglePanel()}
      </div>
    `;
  }

  private _renderSinglePanel() {
    return html`
      <div part="calendar-wrapper">
        <div part="calendar-nav">
          <button part="nav-prev" type="button" aria-label="Previous month"
            @click=${() => this._controller.navigateMonth(-1)}>
            ${CHEVRON_LEFT}
          </button>
          <span part="month-label">${this._controller.primaryMonthLabel}</span>
          <button part="nav-next" type="button" aria-label="Next month"
            @click=${() => this._controller.navigateMonth(1)}>
            ${CHEVRON_RIGHT}
          </button>
        </div>

        <div part="date-input-row">
          <input
            part="date-input"
            type="text"
            aria-label="Date"
            aria-invalid=${this._startInputError ? 'true' : 'false'}
            .value=${this._controller.startInputValue}
            placeholder="Select date"
            @input=${this._handleStartInput}
          />
          <button part="today-button" type="button"
            @click=${() => this._controller.goToToday()}>
            Today
          </button>
        </div>

        ${this._controller.renderCalendar('primary')}
      </div>

      <footer part="footer" class="footer--single">
        <div part="footer-actions">
          <button part="cancel-button" type="button" @click=${this._handleCancel}>Cancel</button>
          <button part="apply-button" type="button" @click=${this._handleApply}>Apply</button>
        </div>
      </footer>
    `;
  }

  private _renderDualPanel() {
    const presets = this._controller.presets;

    return html`
      <div part="panel-body">
        <nav part="preset-sidebar" aria-label="Date range shortcuts">
          ${presets.map(p => html`
            <button
              part="preset-item"
              type="button"
              aria-pressed=${p.active ? 'true' : 'false'}
              @click=${() => this._controller.applyPreset(p.key)}
            >${p.label}</button>
          `)}
        </nav>

        <div class="dual-trailing">
          <div part="calendars">
            <div part="calendar-wrapper">
              <div part="calendar-nav">
                <button part="nav-prev" type="button" aria-label="Previous month"
                  @click=${() => this._controller.navigateMonth(-1)}>
                  ${CHEVRON_LEFT}
                </button>
                <span part="month-label">${this._controller.primaryMonthLabel}</span>
                <button part="nav-next" type="button" class="nav--hidden" aria-hidden="true" tabindex="-1">${CHEVRON_RIGHT}</button>
              </div>
              ${this._controller.renderCalendar('primary')}
            </div>

            <div part="calendar-wrapper">
              <div part="calendar-nav">
                <button part="nav-prev" type="button" class="nav--hidden" aria-hidden="true" tabindex="-1">${CHEVRON_LEFT}</button>
                <span part="month-label">${this._controller.secondaryMonthLabel}</span>
                <button part="nav-next" type="button" aria-label="Next month"
                  @click=${() => this._controller.navigateMonth(1)}>
                  ${CHEVRON_RIGHT}
                </button>
              </div>
              ${this._controller.renderCalendar('secondary')}
            </div>
          </div>

          <footer part="footer">
            <div part="date-inputs">
              <input
                part="date-input"
                type="text"
                aria-label="Start date"
                aria-invalid=${this._startInputError ? 'true' : 'false'}
                .value=${this._controller.startInputValue}
                placeholder="Select date"
                @input=${this._handleStartInput}
              />
              <span part="date-separator" aria-hidden="true">–</span>
              <input
                part="date-input-end"
                type="text"
                aria-label="End date"
                aria-invalid=${this._endInputError ? 'true' : 'false'}
                .value=${this._controller.endInputValue}
                placeholder="Select date"
                @input=${this._handleEndInput}
              />
            </div>
            <div part="footer-actions">
              <button part="apply-button" type="button" @click=${this._handleApply}>Apply</button>
              <button part="cancel-button" type="button" @click=${this._handleCancel}>Cancel</button>
            </div>
          </footer>
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'asc-date-picker': AscDatePicker;
  }
}
